import { useEffect, useRef, useCallback, useState } from 'react';
import { getBrowserClient } from '@/lib/integrations/supabase/client';
import {
  useCacheInvalidation,
  invalidateInbox,
  invalidateServices,
  invalidateStaff
} from '@/lib/features/swr/hooks';
import { invalidateInventory } from '@/lib/features/swr/inventory';
import { invalidateDashboard } from '@/lib/features/swr/dashboard';
import type { CacheInvalidationType } from '@/lib/features/swr/server-invalidation';
import type { RealtimeChannel } from '@supabase/supabase-js';

export type RealtimeConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'reconnecting';

const recentOptimisticUpdates = new Map<string, number>();

let lastEventTimestamp = Date.now();
const HEALTH_CHECK_INTERVAL = 60000;
const STALE_THRESHOLD = 120000;

function updateLastEventTimestamp() {
  lastEventTimestamp = Date.now();
}

export function isRealtimeHealthy(): boolean {
  return Date.now() - lastEventTimestamp < STALE_THRESHOLD;
}

export function markOptimisticUpdate(businessId: string, type: 'earnings' | 'dashboard') {
  const key = `${businessId}:${type}`;
  recentOptimisticUpdates.set(key, Date.now());

  setTimeout(() => {
    recentOptimisticUpdates.delete(key);
  }, 10000);
}

function shouldSkipBroadcastInvalidation(businessId: string, type: 'earnings' | 'dashboard'): boolean {
  const key = `${businessId}:${type}`;
  const timestamp = recentOptimisticUpdates.get(key);

  if (!timestamp) {
    return false;
  }

  const age = Date.now() - timestamp;
  const shouldSkip = age < 10000;
  return shouldSkip;
}

function useDebouncedInvalidation(
  invalidateFn: (businessId: string) => void,
  businessId: string | null,
  delay: number = 500
) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  return useCallback(() => {
    if (!businessId) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      invalidateFn(businessId);
      timeoutRef.current = null;
    }, delay);
  }, [businessId, invalidateFn, delay]);
}

export function useRealtimeSubscription(businessId: string | null) {
  const {
    invalidateBookings,
    invalidateEarnings,
    invalidateSalesSummary,
    invalidateCustomers,
    invalidateAlerts,
    invalidatePayments
  } = useCacheInvalidation();

  const [connectionStatus, setConnectionStatus] = useState<RealtimeConnectionStatus>('connecting');

  const channelRef = useRef<RealtimeChannel | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const baseReconnectDelay = 1000;

  const debouncedInvalidateBookings = useDebouncedInvalidation(invalidateBookings, businessId, 50);
  const debouncedInvalidateCustomers = useDebouncedInvalidation(invalidateCustomers, businessId, 50);
  const debouncedInvalidateEarnings = useDebouncedInvalidation(invalidateEarnings, businessId, 50);
  const debouncedInvalidatePayments = useDebouncedInvalidation(invalidatePayments, businessId, 50);
  const debouncedInvalidateSalesSummary = useDebouncedInvalidation(invalidateSalesSummary, businessId, 50);
  const debouncedInvalidateAlerts = useDebouncedInvalidation(invalidateAlerts, businessId, 50);
  const debouncedInvalidateInbox = useDebouncedInvalidation(invalidateInbox, businessId, 50);
  const debouncedInvalidateDashboard = useDebouncedInvalidation(invalidateDashboard, businessId, 50);

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (channelRef.current) {
      const supabase = getBrowserClient();
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!businessId) return;

    const supabase = getBrowserClient();

    const subscribe = () => {
      setConnectionStatus(reconnectAttempts.current > 0 ? 'reconnecting' : 'connecting');

      const channel = supabase
        .channel(`business-${businessId}`)
        .on('broadcast', { event: 'cache_invalidate' }, (payload) => {
          updateLastEventTimestamp();

          const types = payload.payload.types as CacheInvalidationType[];

          types.forEach(type => {
            switch(type) {
              case 'bookings':
                invalidateBookings(businessId);
                if (!shouldSkipBroadcastInvalidation(businessId, 'dashboard')) {
                  invalidateDashboard(businessId);
                }
                break;
              case 'payments':
                invalidatePayments(businessId);
                if (!shouldSkipBroadcastInvalidation(businessId, 'earnings')) {
                  invalidateEarnings(businessId);
                }
                invalidateSalesSummary(businessId);
                if (!shouldSkipBroadcastInvalidation(businessId, 'dashboard')) {
                  invalidateDashboard(businessId);
                }
                break;
              case 'earnings':
                if (!shouldSkipBroadcastInvalidation(businessId, 'earnings')) {
                  invalidateEarnings(businessId);
                }
                if (!shouldSkipBroadcastInvalidation(businessId, 'dashboard')) {
                  invalidateDashboard(businessId);
                }
                break;
              case 'salesSummary':
                invalidateSalesSummary(businessId);
                break;
              case 'customers':
                invalidateCustomers(businessId);
                break;
              case 'alerts':
                invalidateAlerts(businessId);
                break;
              case 'services':
                invalidateServices(businessId);
                break;
              case 'staff':
                invalidateStaff(businessId);
                break;
              case 'inventory':
                invalidateInventory(businessId);
                break;
              case 'inbox':
                invalidateInbox(businessId);
                break;
              case 'queue':
                invalidateBookings(businessId);
                break;
            }
          });
        })
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'bookings',
            filter: `business_id=eq.${businessId}`
          },
          () => {
            updateLastEventTimestamp();
            debouncedInvalidateBookings();
            debouncedInvalidateCustomers();
            debouncedInvalidateInbox();
            debouncedInvalidateDashboard();
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'payments',
            filter: `business_id=eq.${businessId}`
          },
          () => {
            updateLastEventTimestamp();
            debouncedInvalidateEarnings();
            debouncedInvalidateSalesSummary();
            debouncedInvalidatePayments();
            debouncedInvalidateDashboard();
            debouncedInvalidateBookings();
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'customers',
            filter: `business_id=eq.${businessId}`
          },
          () => {
            updateLastEventTimestamp();
            debouncedInvalidateCustomers();
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'alerts',
            filter: `business_id=eq.${businessId}`
          },
          () => {
            updateLastEventTimestamp();
            debouncedInvalidateAlerts();
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `business_id=eq.${businessId}`
          },
          () => {
            updateLastEventTimestamp();
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'staff_members',
            filter: `business_id=eq.${businessId}`
          },
          () => {
            updateLastEventTimestamp();
            invalidateStaff(businessId);
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'queue_entries',
            filter: `shop_id=eq.${businessId}`
          },
          () => {
            updateLastEventTimestamp();
            debouncedInvalidateBookings();
          }
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            setConnectionStatus('connected');
            reconnectAttempts.current = 0;
            updateLastEventTimestamp();
          } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            setConnectionStatus('disconnected');

            if (reconnectAttempts.current < maxReconnectAttempts) {
              const delay = baseReconnectDelay * Math.pow(2, reconnectAttempts.current);

              reconnectTimeoutRef.current = setTimeout(() => {
                reconnectAttempts.current++;
                cleanup();
                subscribe();
              }, delay);
            }
          }
        });

      channelRef.current = channel;
    };

    subscribe();

    return () => {
      cleanup();
    };
  }, [
    businessId,
    cleanup,
    debouncedInvalidateBookings,
    debouncedInvalidateCustomers,
    debouncedInvalidateEarnings,
    debouncedInvalidatePayments,
    debouncedInvalidateSalesSummary,
    debouncedInvalidateAlerts,
    debouncedInvalidateInbox,
    debouncedInvalidateDashboard,
    invalidateBookings,
    invalidateCustomers,
    invalidateEarnings,
    invalidatePayments,
    invalidateSalesSummary,
    invalidateAlerts
  ]);

  return { connectionStatus };
}
